import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ApiClient from '../utils/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Building2, User, Landmark } from 'lucide-react';
import AddressAutocomplete from '../components/AddressAutocomplete';
import PhoneInput from '../components/PhoneInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClientForm = () => {
  const { clientId } = useParams();
  const isEdit = !!clientId;
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    client_type: 'business',
    billing_email: '',
    billing_address: '',
    billing_address_line2: '',
    po_required: false,
    invoice_consolidation: '',
    notes: '',
    contacts: [],
    locations: [],
    rate_plan: []
  });

  useEffect(() => {
    if (isEdit) {
      fetchClient();
    }
  }, [clientId]);

  const fetchClient = async () => {
    try {
      const response = await fetch(`${API_URL}/api/clients/${clientId}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setFormData(data);
      }
    } catch (error) {
      toast.error('Failed to load client');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Client-side validation
    if (!formData.name || !formData.name.trim()) {
      toast.error(formData.client_type === 'business' ? 'Company name is required' : 'Full name is required');
      setLoading(false);
      return;
    }
    
    try {
      const url = isEdit 
        ? `/api/clients/${clientId}` 
        : `/api/clients`;
      
      const headers = getAuthHeaders();
      const result = isEdit 
        ? await ApiClient.put(url, formData, headers)
        : await ApiClient.post(url, formData, headers);
      
      if (result.ok) {
        toast.success(isEdit ? 'Client updated successfully!' : 'Client created successfully!');
        navigate('/clients');
      } else {
        const errors = ApiClient.parseError(result.data, 'Failed to save client');
        if (Array.isArray(errors)) {
          errors.forEach(err => toast.error(err, { duration: 5000 }));
        } else {
          toast.error(errors, { duration: 5000 });
        }
      }
    } catch (error) {
      console.error('Client save error:', error);
      toast.error(`Failed to save client: ${error.message || 'Network error'}`, { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const addContact = () => {
    setFormData({
      ...formData,
      contacts: [...formData.contacts, { name: '', email: '', phone: '', role: '', is_primary: false }]
    });
  };

  const removeContact = (index) => {
    setFormData({
      ...formData,
      contacts: formData.contacts.filter((_, i) => i !== index)
    });
  };

  const updateContact = (index, field, value) => {
    const contacts = [...formData.contacts];
    contacts[index] = { ...contacts[index], [field]: value };
    setFormData({ ...formData, contacts });
  };

  const addLocation = () => {
    setFormData({
      ...formData,
      locations: [...formData.locations, { name: '', address: '', city: '', state: '', zip_code: '', is_default: false }]
    });
  };

  const removeLocation = (index) => {
    setFormData({
      ...formData,
      locations: formData.locations.filter((_, i) => i !== index)
    });
  };

  const updateLocation = (index, field, value) => {
    const locations = [...formData.locations];
    locations[index] = { ...locations[index], [field]: value };
    setFormData({ ...formData, locations });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="client-form">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/clients')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold font-['Manrope']">
          {isEdit ? 'Edit Client' : 'New Client'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Client Type Selection */}
            <div className="space-y-2">
              <Label>Client Type *</Label>
              <div className="grid grid-cols-3 gap-4">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, client_type: 'business' })}
                  className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                    formData.client_type === 'business'
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid="client-type-business"
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    formData.client_type === 'business' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'
                  }`}>
                    <Building2 className="h-5 w-5" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold">Business</p>
                    <p className="text-xs text-muted-foreground">Company, organization</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, client_type: 'individual' })}
                  className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                    formData.client_type === 'individual'
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid="client-type-individual"
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    formData.client_type === 'individual' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'
                  }`}>
                    <User className="h-5 w-5" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold">Individual</p>
                    <p className="text-xs text-muted-foreground">Private person, self-pay</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, client_type: 'government' })}
                  className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                    formData.client_type === 'government'
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid="client-type-government"
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    formData.client_type === 'government' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'
                  }`}>
                    <Landmark className="h-5 w-5" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold">Government</p>
                    <p className="text-xs text-muted-foreground">Federal, state, local</p>
                  </div>
                </button>
              </div>
            </div>

            <Separator className="my-4" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">{formData.client_type === 'individual' ? 'Full Name' : 'Organization Name'} *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={formData.client_type === 'individual' ? 'Enter full name' : 'Enter organization name'}
                  required
                  data-testid="client-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="billing_email">Billing Email</Label>
                <Input
                  id="billing_email"
                  type="email"
                  value={formData.billing_email}
                  onChange={(e) => setFormData({ ...formData, billing_email: e.target.value })}
                  data-testid="client-email-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="billing_address">Billing Address</Label>
              <AddressAutocomplete
                id="billing_address"
                value={formData.billing_address || ''}
                value2={formData.billing_address_line2 || ''}
                onChange={(value) => setFormData(prev => ({ ...prev, billing_address: value }))}
                onChange2={(value) => setFormData(prev => ({ ...prev, billing_address_line2: value }))}
                placeholder="Enter billing address"
                placeholder2="Suite #, cross street, or other details"
                showLine2={true}
                data-testid="billing-address-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between p-3 rounded-lg border">
                <Label htmlFor="po_required">PO Required</Label>
                <Switch
                  id="po_required"
                  checked={formData.po_required}
                  onCheckedChange={(checked) => setFormData({ ...formData, po_required: checked })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invoice_consolidation">Invoice Consolidation</Label>
                <Select 
                  value={formData.invoice_consolidation} 
                  onValueChange={(value) => setFormData({ ...formData, invoice_consolidation: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (Per Work Order)</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Contacts */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Contacts</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addContact}>
              <Plus className="h-4 w-4 mr-2" /> Add Contact
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.contacts.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No contacts added</p>
            ) : (
              formData.contacts.map((contact, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Contact {index + 1}</span>
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeContact(index)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      placeholder="Name"
                      value={contact.name}
                      onChange={(e) => updateContact(index, 'name', e.target.value)}
                    />
                    <Input
                      placeholder="Email"
                      type="email"
                      value={contact.email}
                      onChange={(e) => updateContact(index, 'email', e.target.value)}
                    />
                    <PhoneInput
                      value={contact.phone}
                      onChange={(value) => updateContact(index, 'phone', value)}
                      placeholder="(555) 555-5555"
                      data-testid={`contact-phone-${index}`}
                    />
                    <Input
                      placeholder="Role/Title"
                      value={contact.role}
                      onChange={(e) => updateContact(index, 'role', e.target.value)}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={contact.is_primary}
                      onCheckedChange={(checked) => updateContact(index, 'is_primary', checked)}
                    />
                    <Label>Primary Contact</Label>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Locations */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Locations</CardTitle>
            <Button type="button" variant="outline" size="sm" onClick={addLocation}>
              <Plus className="h-4 w-4 mr-2" /> Add Location
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.locations.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No locations added</p>
            ) : (
              formData.locations.map((location, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Location {index + 1}</span>
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeLocation(index)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      placeholder="Location Name"
                      value={location.name}
                      onChange={(e) => updateLocation(index, 'name', e.target.value)}
                    />
                    <Input
                      placeholder="Address"
                      value={location.address}
                      onChange={(e) => updateLocation(index, 'address', e.target.value)}
                    />
                    <Input
                      placeholder="City"
                      value={location.city}
                      onChange={(e) => updateLocation(index, 'city', e.target.value)}
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        placeholder="State"
                        value={location.state}
                        onChange={(e) => updateLocation(index, 'state', e.target.value)}
                      />
                      <Input
                        placeholder="ZIP"
                        value={location.zip_code}
                        onChange={(e) => updateLocation(index, 'zip_code', e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <Button type="button" variant="outline" onClick={() => navigate('/clients')}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading} data-testid="save-client-btn">
            {loading ? 'Saving...' : (isEdit ? 'Update Client' : 'Create Client')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ClientForm;
